import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface FAQ {
  question: string;
  answer: string;
}

// FAQ item keys that map to translation keys
const faqKeys = [
  'whatAreBinauralBeats',
  'howDoesTrialWork',
  'needHeadphones',
  'howLongToListen',
  'whenWillISeeResults',
  'canICancelAnytime',
  'areBinauralBeatsSafe',
  'doTheyWorkForEveryone',
];

interface FAQsProps {
  showTitle?: boolean;
  maxItems?: number;
}

const FAQs: React.FC<FAQsProps> = ({ showTitle = true, maxItems }) => {
  const { t } = useTranslation('faqs');
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // Build FAQs from translations
  const faqs: FAQ[] = faqKeys.map((key) => ({
    question: t(`items.${key}.question`),
    answer: t(`items.${key}.answer`),
  }));

  const displayedFaqs = maxItems ? faqs.slice(0, maxItems) : faqs;

  return (
    <div className='faqs-section'>
      {showTitle && (
        <div className='text-center mb-8'>
          <h2 className='subtitle'>{t('title')}</h2>
          <p
            style={{
              textAlign: 'center',
              color: 'var(--text-secondary)',
              fontSize: '16px',
              marginTop: '0px',
            }}
          >
            {t('subtitle')}
          </p>
        </div>
      )}

      <div
        className='faqs-container'
        style={{ maxWidth: '800px', margin: '0 auto' }}
      >
        {displayedFaqs.map((faq, index) => (
          <div
            key={index}
            className='faq-item'
            style={{
              marginBottom: '1rem',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '12px',
              overflow: 'hidden',
              background: 'rgba(255, 255, 255, 0.03)',
            }}
          >
            <button
              onClick={() => toggleFAQ(index)}
              className='faq-question'
              style={{
                width: '100%',
                padding: '1.25rem 1.5rem',
                textAlign: 'left',
                background: 'transparent',
                border: 'none',
                color: 'var(--text-primary)',
                fontSize: '1.05rem',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                transition: 'background 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              <span className='faq-question'>{faq.question}</span>
              <svg
                width='20'
                height='20'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
                style={{
                  transform:
                    openIndex === index ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s ease',
                  flexShrink: 0,
                  marginLeft: '1rem',
                }}
              >
                <polyline points='6 9 12 15 18 9'></polyline>
              </svg>
            </button>
            <div
              className='faq-answer'
              style={{
                maxHeight: openIndex === index ? '500px' : '0',
                overflow: 'hidden',
                transition: 'max-height 0.3s ease, padding 0.3s ease',
                padding:
                  openIndex === index
                    ? '1rem 1.5rem 1.25rem 1.5rem'
                    : '0 1.5rem',
              }}
            >
              <p
                style={{
                  color: 'var(--text-secondary)',
                  lineHeight: '1.7',
                  margin: 0,
                }}
              >
                {faq.answer}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FAQs;
